<template>
  <form class="p-card auth-card" @submit.prevent="submitHandler">
    <div class="form-header mt-3 ml-4 mr-4">
      <div class="logo-container">
        <img alt="logo" class="logo" src="assets/layout/images/logo_kron_tm128.png" width="48" />
        <div class="kron-tm">КРОН-ТМ4</div>
      </div>
      <div class="server-name" data-testid="server_name">{{ serverName }}</div>
    </div>
    <ProgressBar v-if="loading" mode="indeterminate" style="height: 0.5em" />
    <div class="flex flex-column gap-3 px-4 mt-3">
      <div class="flex flex-column">
        <label for="login" class="sr-only">Логин</label>
        <InputText id="login" type="text" v-model.trim="form.login"
          :class="{ 'p-invalid': !isValidateLogin, 'mt-1': true }" />
        <InlineMessage v-if="!isValidateLogin && showInlineMessage">Логин не должен быть пустым</InlineMessage>
      </div>
      <div class="flex flex-column">
        <label for="password" class="sr-only">Пароль</label>
        <Password id="password" :feedback="false" v-model="form.password" toggleMask
          :class="{ 'p-invalid': !isValidatePassword, 'mt-1': true }" />
        <InlineMessage v-if="!isValidatePassword && showInlineMessage">Пароль не менее 3 символов</InlineMessage>
      </div>
      <div class="auth-check">
        <Checkbox v-model="form.checked" binary data-testid="checked_login" />
        <label class="ml-2" style="cursor:pointer" @click="form.checked = !form.checked">Запомнить меня</label>
        <Button style="margin-left: auto" label="" icon="pi pi-sitemap" class="p-button-text"
          @click="showDiagnostic = true" />
      </div>
    </div>
    <div class="flex justify-content-center mt-3 mb-4 text-center">
      <Button class="submit-button" :disabled="loading" type="submit" label="Войти" data-testid="submit_login" />
    </div>
  </form>
  <Diagnostic v-model:showDiagnostic="showDiagnostic" />
</template>

<script setup>
import { reactive, ref, computed, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useRouter, useRoute } from 'vue-router';
import { useToast } from 'primevue/usetoast';
import Diagnostic from '../../pages/common/Diagnostic.vue';

const store = useStore();
const router = useRouter();
const route = useRoute();
const toast = useToast();

const form = reactive({
  login: '',
  password: '',
  checked: false,
});
const serverName = ref('');
const loading = ref(false);
const showDiagnostic = ref(false);
const showInlineMessage = ref(false);

const isValidateLogin = computed(() => form.login !== '');
const isValidatePassword = computed(() => form.password !== '' && form.password.length >= 3);

const submitHandler = async () => {
  showInlineMessage.value = true;
  if (!isValidateLogin.value || !isValidatePassword.value) return;
  loading.value = true;
  showInlineMessage.value = true;

  const payload = {
    login: form.login,
    password: form.password,
    checked: form.checked,
  };

  const service = store.getters.nameToService('BaseClassicService');

  try {
    const response = await service.login({
      userLogin: form.login,
      userPassword: form.password,
    });

    const encryptedPassword = store.getters.crypted(payload.password);
    localStorage.checked = payload.checked;
    localStorage.login = payload.login;
    localStorage.password = encryptedPassword;
    localStorage.token = response.token;
    sessionStorage.setItem('session', true);

    toast.add({
      severity: 'success',
      summary: 'Вход выполнен',
      detail: 'Добро пожаловать в систему',
      life: 3000,
    });

    if (response) {
      const info = {
        login: payload.login,
        password: encryptedPassword,
        checked: payload.checked,
        userId: response.user.userId,
        passwordExpirationDate: response.passwordExpirationDate
          ? new Date(response.passwordExpirationDate)
          : null,
      };
      store.dispatch('fetchInfo', info);
      //store.dispatch('setStructMode', 1);//TODO: удалить при след обнволении через недельку, строка добавлена 20082026
      if (response.passwordExpired) {
        store.dispatch('setPasswordIsOld', true);
        router.push({ path: '/change-password', query: { type: 'system' } });
        return;
      } else {
        store.dispatch('setPasswordIsOld', false);
      }

      if (route.query.redirect) {
        router.push(route.query.redirect);
      } else {
        router.push('/');
      }
    }
  } catch (er) {
    store.commit('SET_ERROR', {
      er: er,
      source: ['Login', 'submitHandler'],
    });
  } finally {
    loading.value = false;
    showInlineMessage.value = false;
  }
};

onMounted(() => {
  form.checked = localStorage.checked === 'true';
  form.login = localStorage.login || '';

  serverName.value = store.getters.const(process.env.VUE_APP_SERVER_NAME);
});
</script>

<style lang="scss" scoped>
:deep(.p-password) {
  display: block;
  width: 100%;
}

:deep(.p-password input) {
  width: 100%;
}

.logo-container {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
}

.kron-tm {
  font-size: 1.9rem;
  margin: 0;
  color: var(--text-color);
  font-weight: bold;
}

.server-name {
  font-size: 2rem;
  margin: 0.5rem 0;
  color: var(--text-color);
  font-weight: bold;
  text-align: center;
}

.form-header {
  text-align: center;
  margin-bottom: 0.7rem;
}

.auth-card {
  min-width: 300px;
  border-radius: 10px;
}

.auth-check {
  display: flex;
  align-items: center;
}

.submit-button {
  width: 88%;
  height: 2.5rem;
  font-weight: bold;
}
</style>
